import { useParams, useNavigate, Link } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { UserRole, formatWineType } from '@wineryxplorer/shared';
import type { TastingExperience, Media } from '@wineryxplorer/shared';
import { HeroCarousel } from '../components/HeroCarousel';
import { ExperienceReviews } from '../components/Experiences/ExperienceReviews';
import { MoreFromVenue } from '../components/Experiences/MoreFromVenue';
import { TastingDetailCTA } from '../components/Experiences/TastingDetailCTA';
import { ReservationChip } from '../components/Experiences/ReservationChip';
import { FavoriteButton } from '../components/FavoriteButton';
import type { MoreFromVenueTasting } from '../components/Experiences/MoreFromVenue';

export const TastingExperienceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: experienceData, isLoading, error } = trpc.tastingExperience.getById.useQuery(
    { id: id || '' },
    { enabled: !!id }
  );

  // Type the experience properly - wines can be populated objects or strings
  const experience = experienceData as (TastingExperience & {
    venue?: {
      name: string;
      address?: {
        city?: string;
        state?: string;
      };
    };
    wines?: Array<string | {
      _id: string;
      name: string;
      wineType?: string;
      vintage?: number;
    }>;
    includesFood?: boolean;
  }) | undefined;

  // Helper to normalize wine data
  const normalizeWines = (wines?: Array<string | { _id: string; name: string; wineType?: string; vintage?: number }>) => {
    if (!wines) return [];
    return wines
      .map((wine) => {
        if (typeof wine === 'string') {
          return null; // Skip string IDs
        }
        return {
          _id: wine._id,
          name: wine.name,
          wineType: wine.wineType,
          vintage: wine.vintage,
        };
      })
      .filter((wine): wine is NonNullable<typeof wine> => wine !== null);
  };

  const normalizedWines = normalizeWines(experience?.wines);

  // Extract venueId as string for queries
  const venueIdString =
    experience && typeof experience.venueId === 'string'
      ? experience.venueId
      : experience && typeof experience.venueId === 'object'
        ? (experience.venueId as unknown as { _id?: string; id?: string })._id ||
          (experience.venueId as unknown as { _id?: string; id?: string }).id
        : undefined;

  // Fetch full venue details for CTA, map, and reservation policy
  const { data: venueData } = trpc.venue.getById.useQuery(
    { id: venueIdString || '' },
    { enabled: !!venueIdString }
  );

  // Fetch other tastings from same venue for "More from" section
  const { data: otherTastings } = trpc.tastingExperience.listByVenue.useQuery(
    { venueId: venueIdString || '', limit: 10 },
    { enabled: !!venueIdString }
  );

  // Collect all media IDs from the experience
  const allMediaIds = experience
    ? [
        ...(experience.heroImageId ? [experience.heroImageId] : []),
        ...experience.imageIds,
        ...experience.videoIds,
      ]
    : [];

  // Fetch media by IDs
  const { data: mediaData } = trpc.media.getByIds.useQuery(
    { ids: allMediaIds },
    { enabled: allMediaIds.length > 0 }
  );

  // Cast media array to proper type
  const mediaList = (mediaData ?? []) as Media[];

  // Build ordered image list for hero (hero first, then gallery)
  const getHeroMediaList = (): Media[] => {
    if (!mediaList.length) return [];
    const result: Media[] = [];
    if (experience?.heroImageId) {
      const hero = mediaList.find((m) => m.id === experience.heroImageId);
      if (hero) result.push(hero);
    }
    if (experience?.imageIds.length) {
      const gallery = mediaList.filter((m) => experience.imageIds.includes(m.id));
      result.push(...gallery);
    }
    return result;
  };

  const heroMediaList = getHeroMediaList();

  // Venue hero image URL for fallback / More-from cards
  const venueHeroUrl = venueData?.firstHeroImage?.cloudinaryUrl ?? null;

  // Static map URL derived from venue coordinates
  const venueCoords = venueData?.address?.coordinates;
  const mapUrl =
    venueCoords && import.meta.env.VITE_GOOGLE_MAPS_API_KEY
      ? `https://maps.googleapis.com/maps/api/staticmap?center=${venueCoords.lat},${venueCoords.lng}&zoom=14&size=600x300&markers=color:0x6B1D2B%7C${venueCoords.lat},${venueCoords.lng}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
      : null;

  // Duration formatter
  const formatDurationMin = (mins: number) => {
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="px-0 md:px-4 pt-2 pb-8 max-w-4xl mx-auto">
          <div className="text-center py-8">
            <p className="text-gray-600">Loading tasting experience...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (error || !experience) {
    return (
      <PageContainer>
        <div className="px-0 md:px-4 pt-2 pb-8 max-w-4xl mx-auto">
          <div className="text-center py-8">
            <p className="text-red-600">
              {error?.message || 'Tasting experience not found'}
            </p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Check if user has permission to edit
  const canManage =
    user &&
    (user.role === UserRole.SUPER_ADMIN ||
      user.role === UserRole.SITE_ADMIN ||
      user.role === UserRole.VENUE_ADMIN);

  const venueId = venueIdString ?? '';
  const venueName = venueData?.name ?? experience.venue?.name ?? 'this winery';

  return (
    <PageContainer>
      <div className="px-0 md:px-4 pt-2 pb-8 max-w-4xl mx-auto">

        {/* Edit button for authorized users */}
        {canManage && (
          <div className="flex justify-end mb-3 px-3 sm:px-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/tasting-experiences/${id}/edit`)}
            >
              Edit
            </Button>
          </div>
        )}

        {/* Hero */}
        {heroMediaList.length > 0 ? (
          <HeroCarousel
            images={heroMediaList}
            venueName={venueName}
            venueType={venueData?.venueType ?? 'winery'}
            showControls={heroMediaList.length > 1}
            withPaginationDots={heroMediaList.length > 1}
          />
        ) : venueHeroUrl ? (
          <img
            src={venueHeroUrl}
            alt={venueName}
            className="w-full h-64 md:h-96 object-cover rounded-lg"
          />
        ) : (
          <div className="w-full h-64 md:h-96 bg-gray-100 rounded-lg" />
        )}

        {/* Header block */}
        <div className="px-3 sm:px-0 mt-6">
          <h1 className="font-serif text-3xl sm:text-4xl font-normal leading-tight text-gray-900">
            {experience.name}
          </h1>
          {venueId && (
            <Link
              to={`/wineries/${venueId}`}
              className="inline-block mt-1 text-sm font-sans tracking-wide text-secondary-700 hover:text-secondary-800"
            >
              {venueName}
            </Link>
          )}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {experience.price && (
              <span className="font-serif text-2xl font-normal text-gray-900">
                ${experience.price.amount}
                <span className="ml-1 text-xs font-sans tracking-widest uppercase text-gray-500">per guest</span>
              </span>
            )}
            {experience.duration && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-sm font-sans tracking-wide text-gray-700">
                  {formatDurationMin(experience.duration)}
                </span>
              </>
            )}
            <ReservationChip
              requiresReservation={Boolean(experience.requiresReservation)}
              venuePolicy={
                (venueData as { reservationPolicy?: 'required' | 'recommended' | 'drop-ins-welcome' } | undefined)
                  ?.reservationPolicy ?? null
              }
            />
            <div className="ml-auto">
              <FavoriteButton
                entityType="tastingExperience"
                entityId={experience.id ?? (experience as unknown as { _id?: string })._id ?? ''}
              />
            </div>
          </div>
        </div>

        {/* Primary CTA */}
        <div className="px-3 sm:px-0 mt-6">
          <TastingDetailCTA
            experienceBookingUrl={
              (experience as unknown as { bookingUrl?: string }).bookingUrl ?? null
            }
            venueBookingUrl={
              (venueData as { bookingUrl?: string } | undefined)?.bookingUrl ?? null
            }
            venueWebsite={venueData?.website ?? null}
            venuePhone={venueData?.phone ?? null}
            venueId={venueId}
            venueName={venueName}
          />
        </div>

        {/* Description */}
        {experience.description && (
          <div className="px-3 sm:px-0 mt-8">
            <p className="font-serif text-lg leading-relaxed text-gray-800 whitespace-pre-line">
              {experience.description}
            </p>
          </div>
        )}

        {/* Wines You'll Taste */}
        {normalizedWines.length > 0 && (
          <div className="px-3 sm:px-0 mt-10">
            <h2 className="font-serif text-2xl font-normal text-gray-900 mb-4">
              Wines You&apos;ll Taste
            </h2>
            <ul className="space-y-2">
              {normalizedWines.map((wine) => (
                <li key={wine._id}>
                  <Link
                    to={`/wines/${wine._id}`}
                    className="block px-4 py-3 rounded-md border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    <div className="font-serif text-lg text-gray-900">{wine.name}</div>
                    <div className="text-xs font-sans tracking-widest uppercase text-gray-500 mt-1">
                      {wine.wineType ? formatWineType(wine.wineType as never) : ''}
                      {wine.vintage ? ` · ${wine.vintage}` : ''}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* More from [Winery] */}
        <MoreFromVenue
          items={(otherTastings?.items ?? []) as MoreFromVenueTasting[]}
          currentExperienceId={experience.id ?? (experience as unknown as { _id?: string })._id}
          venueName={venueName}
          venueHeroUrl={venueHeroUrl}
          venuePolicy={
            (venueData as { reservationPolicy?: 'required' | 'recommended' | 'drop-ins-welcome' } | undefined)
              ?.reservationPolicy ?? null
          }
        />

        {/* Map — Find Your Way */}
        {mapUrl && venueData?.address && (
          <div className="px-3 sm:px-0 mt-10">
            <h2 className="font-serif text-2xl font-normal text-gray-900 mb-4">Find Your Way</h2>
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(
                `${venueName}, ${venueData.address.street ?? ''}, ${venueData.address.city ?? ''}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-lg overflow-hidden border border-gray-200"
            >
              <img
                src={mapUrl}
                alt={`Map of ${venueName}`}
                className="w-full"
              />
            </a>
          </div>
        )}

        {/* Reviews */}
        <div className="px-3 sm:px-0 mt-10">
          <ExperienceReviews
            experienceId={experience.id ?? (experience as unknown as { _id?: string })._id ?? ''}
            experienceName={experience.name}
            includesFood={false}
          />
        </div>

      </div>
    </PageContainer>
  );
};
